/**
 * CalloutAnnotation Component
 * Creates a visible callout annotation that highlights text in the PDF.
 * Uses direct DOM manipulation to position elements correctly.
 * 
 * Handles zoom/page changes by re-finding elements when the PDF re-renders.
 * When multiple matches exist, finds the one closest to the viewport center.
 */

import React, { useEffect, useState, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Paper, Typography, Chip, IconButton, Box } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { BOMItem } from '../../types/bom.types';

interface CalloutAnnotationProps {
  item: BOMItem | null;
  pageNumber: number;
  containerRef: React.RefObject<HTMLDivElement>;
  onClose?: () => void;
  /** Optional: current scale value to detect zoom changes */
  scale?: number;
}

// Color mapping for component types
const TYPE_COLORS: Record<string, string> = {
  MCB: '#2196f3',
  CONTACTOR: '#4caf50',
  RELAY: '#ff9800',
  TERMINAL: '#9c27b0',
  PLC: '#e91e63',
  MOTOR: '#00bcd4',
  FUSE: '#ff5722',
  TRANSFORMER: '#795548',
  POWER_SUPPLY: '#607d8b',
  GENERATOR: '#ffc107',
  FAN: '#009688',
  FILTER: '#03a9f4',
  HMI: '#673ab7',
  IO_MODULE: '#3f51b5',
  CONTROLLER: '#8bc34a',
  OTHER: '#9e9e9e',
};

const getTypeColor = (componentType?: string): string => {
  return TYPE_COLORS[componentType?.toUpperCase() || 'OTHER'] || TYPE_COLORS.OTHER;
};

const CalloutAnnotation: React.FC<CalloutAnnotationProps> = ({
  item,
  pageNumber,
  containerRef,
  onClose,
  scale,
}) => {
  const [targetElement, setTargetElement] = useState<HTMLElement | null>(null);
  const [calloutPosition, setCalloutPosition] = useState<{ x: number; y: number } | null>(null);
  const highlightRef = useRef<HTMLDivElement | null>(null);
  const lineRef = useRef<SVGSVGElement | null>(null);
  const lastScaleRef = useRef<number | undefined>(scale);
  const mutationObserverRef = useRef<MutationObserver | null>(null);
  const resizeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isScrollingRef = useRef<boolean>(false); // Prevent re-finding during scroll

  // Find ALL matching text elements in the PDF
  const findAllMatchingElements = useCallback((): HTMLElement[] => {
    if (!item?.reference) return [];

    const textLayer = document.querySelector(
      `.react-pdf__Page[data-page-number="${pageNumber}"] .react-pdf__Page__textContent`
    );

    if (!textLayer) return [];

    const spans = Array.from(textLayer.querySelectorAll('span')) as HTMLElement[];
    const ref = item.reference.toUpperCase();
    const matches: HTMLElement[] = [];
    
    for (const span of spans) {
      const text = (span.textContent || '').toUpperCase();
      let isMatch = false;
      
      // STRICT matching rules:
      // 1. Exact match
      if (text === ref || text.trim() === ref) {
        isMatch = true;
      }
      
      // 2. Text contains reference as a standalone word (with word boundaries)
      if (!isMatch) {
        const wordBoundaryPattern = new RegExp(
          `(^|[-\\s/\\(])${ref.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}($|[\\s:/\\),])`,
          'i'
        );
        if (wordBoundaryPattern.test(text)) {
          isMatch = true;
        }
      }
      
      // 3. Reference with dash prefix (e.g., "-FLT1")
      if (!isMatch && (text === `-${ref}` || text.includes(`-${ref}`))) {
        isMatch = true;
      }
      
      if (isMatch) {
        matches.push(span);
      }
    }
    
    return matches;
  }, [item, pageNumber]);

  // Find the BEST matching element - closest to viewport center
  const findTextElement = useCallback((): HTMLElement | null => {
    const matches = findAllMatchingElements();
    
    if (matches.length === 0) return null;
    if (matches.length === 1) return matches[0];
    
    // Multiple matches - find the one closest to the center of the visible viewport
    const container = containerRef.current;
    let centerX = window.innerWidth / 2;
    let centerY = window.innerHeight / 2;
    
    // If we have a container, use its visible center
    if (container) {
      const containerRect = container.getBoundingClientRect();
      centerX = containerRect.left + containerRect.width / 2;
      centerY = containerRect.top + containerRect.height / 2;
    }
    
    let bestMatch: HTMLElement | null = null;
    let bestDistance = Infinity;
    
    for (const match of matches) {
      const rect = match.getBoundingClientRect();
      
      // Skip elements that are not visible
      if (rect.width === 0 || rect.height === 0) continue;
      
      const matchCenterX = rect.left + rect.width / 2;
      const matchCenterY = rect.top + rect.height / 2;
      
      // Calculate distance to viewport center
      const distance = Math.sqrt(
        Math.pow(matchCenterX - centerX, 2) + 
        Math.pow(matchCenterY - centerY, 2)
      );
      
      // Prefer elements that are actually in the visible viewport
      const isInViewport = (
        rect.top >= 0 && 
        rect.left >= 0 && 
        rect.bottom <= window.innerHeight && 
        rect.right <= window.innerWidth
      );
      
      // Boost score for in-viewport elements
      const adjustedDistance = isInViewport ? distance : distance + 10000;
      
      if (adjustedDistance < bestDistance) {
        bestDistance = adjustedDistance;
        bestMatch = match;
      }
    }
    
    return bestMatch || matches[0];
  }, [findAllMatchingElements, containerRef]);

  // Cleanup function to remove all visual elements
  const cleanup = useCallback(() => {
    if (highlightRef.current) {
      highlightRef.current.remove();
      highlightRef.current = null;
    }
    if (lineRef.current) {
      lineRef.current.remove();
      lineRef.current = null;
    }
    setCalloutPosition(null);
    setTargetElement(null);
  }, []);

  // Create and position the highlight overlay
  const createHighlight = useCallback((element: HTMLElement) => {
    // Remove any existing highlight first
    if (highlightRef.current) {
      highlightRef.current.remove();
      highlightRef.current = null;
    }
    if (lineRef.current) {
      lineRef.current.remove();
      lineRef.current = null;
    }

    // Get the PDF page container to use as positioning reference
    const pdfPage = document.querySelector(
      `.react-pdf__Page[data-page-number="${pageNumber}"]`
    ) as HTMLElement;
    
    if (!pdfPage) {
      setCalloutPosition(null);
      return;
    }

    const pageRect = pdfPage.getBoundingClientRect();
    const elemRect = element.getBoundingClientRect();
    
    // Calculate position relative to PDF page
    const relativeLeft = elemRect.left - pageRect.left;
    const relativeTop = elemRect.top - pageRect.top;
    
    const typeColor = getTypeColor(item?.component_type);

    // Create highlight div - positioned absolutely within PDF page
    const highlight = document.createElement('div');
    highlight.className = 'callout-highlight';
    highlight.style.cssText = `
      position: absolute;
      left: ${relativeLeft - 6}px;
      top: ${relativeTop - 4}px;
      width: ${elemRect.width + 12}px;
      height: ${elemRect.height + 8}px;
      border: 4px solid ${typeColor};
      border-radius: 6px;
      background: ${typeColor}30;
      pointer-events: none;
      z-index: 10000;
      box-shadow: 0 0 20px ${typeColor}80, inset 0 0 10px ${typeColor}40;
      animation: callout-pulse 1.5s ease-in-out infinite;
    `;
    
    // Ensure PDF page has relative positioning for absolute children
    if (getComputedStyle(pdfPage).position === 'static') {
      pdfPage.style.position = 'relative';
    }
    pdfPage.appendChild(highlight);
    highlightRef.current = highlight;

    // Calculate callout position - to the LEFT of the highlight
    const calloutWidth = 160;
    const calloutHeight = 80;
    let calloutLeft = relativeLeft - calloutWidth - 30; // 30px gap
    let calloutTop = relativeTop - 10;
    
    // If not enough room on left, position on right
    if (calloutLeft < 10) {
      calloutLeft = relativeLeft + elemRect.width + 30;
    }
    
    // Keep within vertical bounds
    if (calloutTop < 10) {
      calloutTop = 10;
    }

    // Create SVG for the connecting line - also absolute within PDF page
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.style.cssText = `
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      pointer-events: none;
      z-index: 9999;
      overflow: visible;
    `;
    
    // Calculate line coordinates (relative to PDF page)
    const lineStartX = calloutLeft + calloutWidth;
    const lineStartY = calloutTop + 35;
    const lineEndX = relativeLeft - 8;
    const lineEndY = relativeTop + elemRect.height / 2;

    svg.innerHTML = `
      <defs>
        <marker id="callout-arrow-${pageNumber}" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
          <polygon points="0 0, 10 3.5, 0 7" fill="${typeColor}"/>
        </marker>
      </defs>
      <line 
        x1="${lineStartX}" y1="${lineStartY}" 
        x2="${lineEndX}" y2="${lineEndY}"
        stroke="${typeColor}" 
        stroke-width="3" 
        marker-end="url(#callout-arrow-${pageNumber})"
      />
      <circle cx="${lineEndX}" cy="${lineEndY}" r="5" fill="${typeColor}" stroke="white" stroke-width="2"/>
    `;
    pdfPage.appendChild(svg);
    lineRef.current = svg;

    // Set callout position (relative to PDF page, for the React portal)
    setCalloutPosition({ x: calloutLeft, y: calloutTop });

    // Scroll element into view
    element.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
      inline: 'center',
    });
  }, [item?.component_type, pageNumber]);

  // Update positions - not needed with absolute positioning within PDF page
  // The elements move with the PDF page automatically
  const updatePositions = useCallback(() => {
    // With absolute positioning within the PDF page, elements move with the page
    // No need to update positions on scroll
  }, []);

  // Clear callout when item or page changes
  useEffect(() => {
    // Always cleanup when item or page changes
    cleanup();
  }, [item?.reference, pageNumber, cleanup]);

  // Re-find element when scale changes
  useEffect(() => {
    if (scale !== lastScaleRef.current && item) {
      lastScaleRef.current = scale;
      
      // Cleanup existing highlight
      if (highlightRef.current) {
        highlightRef.current.remove();
        highlightRef.current = null;
      }
      if (lineRef.current) {
        lineRef.current.remove();
        lineRef.current = null;
      }
      setTargetElement(null);
      setCalloutPosition(null);
      
      // Re-find element after zoom animation settles
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
      
      resizeTimeoutRef.current = setTimeout(() => {
        const element = findTextElement();
        if (element) {
          setTargetElement(element);
          createHighlight(element);
        }
      }, 300);
    }
  }, [scale, item, findTextElement, createHighlight]);

  // Main effect to find and highlight text
  useEffect(() => {
    if (!item) {
      cleanup();
      return;
    }

    // Try to find element multiple times (PDF might still be rendering)
    const attempts = [100, 300, 500, 800, 1200];
    const timers: NodeJS.Timeout[] = [];
    let found = false;

    attempts.forEach((delay) => {
      const timer = setTimeout(() => {
        if (found) return; // Already found, skip
        
        const element = findTextElement();
        if (element) {
          found = true;
          setTargetElement(element);
          createHighlight(element);
        }
      }, delay);
      timers.push(timer);
    });

    return () => {
      timers.forEach(clearTimeout);
    };
  }, [item, pageNumber, findTextElement, createHighlight, cleanup]);

  // Watch for PDF page mutations (re-renders after zoom)
  useEffect(() => {
    if (!item) return;

    const pdfPage = document.querySelector(
      `.react-pdf__Page[data-page-number="${pageNumber}"]`
    );

    if (!pdfPage) return;

    // Disconnect any existing observer
    if (mutationObserverRef.current) {
      mutationObserverRef.current.disconnect();
    }

    const observer = new MutationObserver((mutations) => {
      // Skip if we're in the middle of a scroll animation
      if (isScrollingRef.current) return;
      
      // Check if text layer was modified
      const hasTextLayerChange = mutations.some(m => 
        m.type === 'childList' && 
        (m.target as HTMLElement).classList?.contains('react-pdf__Page__textContent')
      );

      if (hasTextLayerChange) {
        // PDF text layer re-rendered, update positions (don't re-find to avoid jumping)
        if (resizeTimeoutRef.current) {
          clearTimeout(resizeTimeoutRef.current);
        }
        
        resizeTimeoutRef.current = setTimeout(() => {
          // Only update positions, don't re-find element to prevent jumping
          if (targetElement) {
            updatePositions();
          } else {
            // Only find element if we don't have one yet
            const element = findTextElement();
            if (element) {
              setTargetElement(element);
              createHighlight(element);
            }
          }
        }, 150);
      }
    });

    observer.observe(pdfPage, {
      childList: true,
      subtree: true,
    });

    mutationObserverRef.current = observer;

    return () => {
      observer.disconnect();
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
    };
  }, [item, pageNumber, findTextElement, createHighlight, targetElement, updatePositions]);

  // Update positions on scroll
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleScroll = () => {
      // Debounce scroll updates
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
      resizeTimeoutRef.current = setTimeout(updatePositions, 16);
    };
    
    const handleResize = () => {
      // Re-find element on resize as positions may have shifted significantly
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
      resizeTimeoutRef.current = setTimeout(() => {
        if (item) {
          const element = findTextElement();
          if (element) {
            setTargetElement(element);
            createHighlight(element);
          }
        }
      }, 200);
    };

    container.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);

    return () => {
      container.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, [containerRef, updatePositions, item, findTextElement, createHighlight]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanup();
      if (mutationObserverRef.current) {
        mutationObserverRef.current.disconnect();
      }
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
    };
  }, [cleanup]);

  // Add CSS animation styles
  useEffect(() => {
    const styleId = 'callout-animation-styles';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        @keyframes callout-pulse {
          0%, 100% {
            box-shadow: 0 0 15px currentColor, inset 0 0 8px currentColor;
            transform: scale(1);
          }
          50% {
            box-shadow: 0 0 25px currentColor, inset 0 0 12px currentColor;
            transform: scale(1.02);
          }
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  // Don't render if no item, no position, or no target element found
  if (!item || !calloutPosition || !targetElement) return null;

  // Find PDF page to render callout within
  const pdfPage = document.querySelector(
    `.react-pdf__Page[data-page-number="${pageNumber}"]`
  );
  
  if (!pdfPage) return null;

  const typeColor = getTypeColor(item.component_type);

  // Render callout using portal into the PDF page (so it scrolls with the page)
  return createPortal(
    <Paper
      elevation={16}
      sx={{
        position: 'absolute',
        left: calloutPosition.x,
        top: calloutPosition.y,
        width: 160,
        zIndex: 10001,
        p: 1,
        backgroundColor: 'white',
        border: `3px solid ${typeColor}`,
        borderRadius: 2,
        boxShadow: `0 4px 16px rgba(0,0,0,0.3), 0 0 0 2px white`,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        <Box sx={{ flex: 1 }}>
          {/* Component Reference */}
          <Typography
            variant="h6"
            sx={{
              fontWeight: 'bold',
              color: typeColor,
              fontFamily: 'monospace',
              fontSize: '1.1rem',
              mb: 0.5,
            }}
          >
            {item.reference}
          </Typography>

          {/* Component Type Chip */}
          <Chip
            label={item.component_type || 'UNKNOWN'}
            size="small"
            sx={{
              backgroundColor: typeColor,
              color: 'white',
              fontWeight: 'bold',
              fontSize: '0.65rem',
              height: 20,
            }}
          />

          {/* Page info */}
          <Typography
            variant="caption"
            display="block"
            sx={{ color: 'text.secondary', fontSize: '0.65rem', mt: 0.5 }}
          >
            Page {item.page || (item.pages && item.pages[0]) || pageNumber}
          </Typography>
        </Box>

        {/* Close button */}
        {onClose && (
          <IconButton
            size="small"
            onClick={onClose}
            sx={{
              p: 0.25,
              color: 'grey.500',
              '&:hover': { color: 'error.main', bgcolor: 'error.light' },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
    </Paper>,
    pdfPage
  );
};

export default CalloutAnnotation;
